'use client';

import * as React from 'react';
import type { TransitionProps, TransitionStatus } from 'notistack';
import { Transition } from 'notistack';
import {
    useForkRef,
    reflow,
    getTransitionProps,
    createTransition,
} from './shared';

const styles: Partial<Record<TransitionStatus, React.CSSProperties>> = {
    entering: {
        opacity: 1,
        transform: 'translateY(0)',
    },
    entered: {
        opacity: 1,
        transform: 'translateY(0)',
    },
};

export const FadeSlide = React.forwardRef<HTMLDivElement, TransitionProps>(
    (props, ref) => {
        const {
            children,
            in: inProp,
            timeout = 300, // Default timeout
            style,
            onEnter,
            onEntered,
            onExit,
            onExited,
            ...other
        } = props;

        const nodeRef = React.useRef<HTMLDivElement>(null);
        const handleRefIntermediary = useForkRef<HTMLDivElement>(
            (children as React.RefAttributes<HTMLDivElement>)?.ref,
            ref
        );
        const handleRef = useForkRef(nodeRef, handleRefIntermediary);

        const handleEnter: TransitionProps['onEnter'] = (node, isAppearing) => {
            reflow(node);

            const transitionProps = getTransitionProps({
                style,
                timeout,
                mode: 'enter',
            });
            node.style.transition = createTransition(transitionProps, [
                'opacity',
                'transform',
            ]);

            if (onEnter) {
                onEnter(node, isAppearing);
            }
        };

        const handleExit: TransitionProps['onExit'] = node => {
            const transitionProps = getTransitionProps({
                style,
                timeout,
                mode: 'exit',
            });
            node.style.transition = createTransition(transitionProps, [
                'opacity',
                'transform',
            ]);

            if (onExit) {
                onExit(node);
            }
        };

        return (
            <Transition
                appear
                in={inProp}
                nodeRef={nodeRef}
                onEnter={handleEnter}
                onEntered={onEntered}
                onExit={handleExit}
                onExited={onExited}
                timeout={timeout}
                {...other}
            >
                {(status, childProps) =>
                    React.cloneElement(children as React.ReactElement, {
                        style: {
                            opacity: 0,
                            transform: 'translateY(20px)',
                            visibility:
                                status === 'exited' && !inProp
                                    ? 'hidden'
                                    : undefined,
                            ...styles[status],
                            ...style,
                            ...(children as React.ReactElement).props.style,
                        },
                        ref: handleRef,
                        ...childProps,
                    })
                }
            </Transition>
        );
    }
);

FadeSlide.displayName = 'FadeSlide';
